import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map } from 'maplibre-gl';
import {
  buildLiguriaStyle,
  buildMinimalLiguriaStyle,
  buildFallbackRasterStyle,
  buildMapoutStyle,
  trailLayerSpec,
  resolveTrailStyle,
  TRAILS_SOURCE_NAME,
  TRAILS_HIT_LAYER_ID,
  routeLayerSpec,
  CORRIDOR_SOURCE_ID,
  NAVI_SEGMENTS_SOURCE_ID,
  type FallbackStyleName,
} from './style';
import { getRegionMapData } from '../../data/corridorData';
import type { RegionMapData } from '../../data/corridorData';
import { getCorridorOverlays } from '../corridorOverlays';
import {
  addPmtilesArchive,
  prewarmPmtilesArchives,
  registerPmtilesProtocol,
} from './pmtiles';
import { opfsStatus } from '../opfsStore';
import { LocateControl } from './LocateControl';
import { CompassRose } from './CompassRose';
import { mapBus } from './mapBus';
import { locateControlBus } from './locateControlBus';
import { compassBus } from './compassBus';
import { PREF_EXPLORE_ZOOM, EXPLORE_ZOOM_DEFAULT } from '../useRideSettings';
// Wave 14ca-fix64: Color-Assist-Modus. Hook hier halten damit
// MapView re-rendert wenn der User den Toggle flippt — dadurch laufen alle
// gestreuten DIFFICULTY_COLOR-Lookups (resolveTrailStyle, Aura, etc.) in
// der naechsten Render-Pass mit der neuen Palette.
import { useActiveRegion, useColorAssistMode } from '../useUiSettings';
import { REGIONS, CORRIDOR_CENTER } from '@adtu/shared';
import { useCurrentPosition } from '../useCurrentPosition';
import { cumulativeDistances, nearestPointOnTrack, sliceTrackBetween } from '../trailGeom';
import type { Difficulty, GpxTrack } from '@adtu/shared';
import { trackToGeoJson, difficultyAuraColor } from '@adtu/shared';
import { tilesManifest } from '../api';

export interface MapViewProps {
  trails?: Array<{
    id: string;
    difficulty: Difficulty;
    colorHex?: string | null;
    track: GpxTrack;
  }>;
  onTrailClick?: (trailId: string) => void;
  /** Wenn gesetzt, kriegt dieser Trail eine Highlight-Aura, Richtungspfeile
   *  und Start (grün) / End (rot) Marker. */
  selectedTrailId?: string | null;
  /**
   * Wave 14ah: Override für die Aura-/Start-/End-Geometrie. Wird benutzt, wenn
   * die Selektion ein zusammengesetzter "Run" ist, der in mehrere farbige
   * Trail-Segmente zerlegt wurde (siehe sliceRunIntoSegments in runAsTrail.ts).
   * In dem Fall steht `selectedTrailId` gar nicht in `trails`, weil wir dort
   * die einzelnen Segmente haben. Mit diesem Override zeichnen wir aber
   * trotzdem EINE zusammenhängende Aura (von Run-Start bis Run-Ende) und EIN
   * Paar Start/End-Marker.
   *
   * Wenn gesetzt UND selectedTrailId gesetzt ist → dieser Override gewinnt.
   * Wenn selectedTrailId null/undefined ist, wird die Aura wie immer NICHT
   * gezeichnet (auch bei gesetztem Override — der Override liefert nur
   * Geometrie, kein Selection-Signal).
   */
  selectionOverride?: {
    track: GpxTrack;
    difficulty: Difficulty;
    colorHex?: string | null;
  } | null;
  /**
   * Wave 14cf — Ride-Next-Aura.
   *
   * Wenn > 0 und der ausgewählte Trail/Override eine Geometrie hat, zeichnet
   * die Map zusätzlich eine orange Aura auf den nächsten N Metern entlang
   * der Route ab der aktuellen GPS-Position. Hilft auf komplexen Routen
   * (Loops, Self-Crossings) zu sehen wo's weitergeht.
   *
   * 0 = Feature aus. User-konfigurierbar im Profile (default 500m).
   */
  rideNextAuraMeters?: number;
  fallbackStyle?: FallbackStyleName;
}

interface ManifestUrls {
  mapUrl: string;
  contourUrl?: string;
  hillshadeUrl?: string;
}

/**
 * Aktiviert Terrain mit exaggeration=0 — visuell UNSICHTBAR, aber
 * map.queryTerrainElevation(lngLat) funktioniert danach. Wird vom
 * ElevationBadge + useTrailElevation als SRTM-Fallback benutzt, wenn GPS
 * keine Altitude liefert oder ein Trail-GPX keine Höhenwerte hat.
 *
 * Wave 14aq (Elevation-Profile-Fix):
 *   - Source-Name war 'hillshade', tatsächlich heisst die DEM-Source in
 *     style.ts aber 'dem'. Durch den Name-Mismatch wurde setTerrain seit
 *     jeher NIE gecallt → queryTerrainElevation gab immer null zurück →
 *     Höhenprofil für GPX ohne ele blieb flach. User-Bug "Appendaun flat".
 *   - Wenn die aktuelle Style gar keine DEM-Source hat (z.B. 'minimal',
 *     'outdoor'), fügen wir sie jetzt programmatisch hinzu — über den
 *     hillshadeUrl aus dem Manifest falls offline-PMTiles vorhanden, sonst
 *     über den AWS-Terrarium-Online-Fallback. Die Source ist ausschliesslich
 *     für queryTerrainElevation da (kein visibler hillshade-Layer in diesen
 *     Modi), deswegen "silent".
 *
 * Fehler-Cases:
 *   - Offline + style ohne lokale PMTiles + AWS nicht erreichbar → wir
 *     setzen trotzdem setTerrain auf, queryTerrainElevation returnt dann
 *     null und useTrailElevation zeigt source='none'. Das ist ok.
 */
function enableSilentTerrain(map: Map, urls?: ManifestUrls | null): void {
  try {
    // Schritt 1: DEM-Source sicherstellen (ggf. nachträglich hinzufügen).
    if (!map.getSource('dem')) {
      if (urls?.hillshadeUrl) {
        // Lokale offline-fähige PMTiles — rio-rgbify default-encoded (mapbox).
        map.addSource('dem', {
          type: 'raster-dem',
          url: urls.hillshadeUrl,
          tileSize: 256,
          encoding: 'mapbox',
        });
      } else {
        // AWS Terrarium-Online-Fallback (CC0, braucht Netz).
        map.addSource('dem', {
          type: 'raster-dem',
          tiles: [
            'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
          ],
          tileSize: 256,
          encoding: 'terrarium',
          maxzoom: 15,
        });
      }
    }
    // Schritt 2: Terrain aktivieren. Wave 14aq: exaggeration minimal statt
    // exakt 0, weil manche MapLibre-Versionen exaggeration=0 intern zu
    // "terrain disabled" short-circuiten und dann queryTerrainElevation
    // null zurückgibt — auch wenn setTerrain erfolgreich gecallt wurde.
    // 0.0001 ist visuell völlig unsichtbar (sub-pixel), aktiviert aber die
    // Terrain-Mesh-Berechnung die queryTerrainElevation braucht.
    //
    // setTerrain ist idempotent — wenn schon gesetzt, überschreibt es sauber.
    map.setTerrain({ source: 'dem', exaggeration: 0.0001 });
  } catch (err) {
    console.warn('[map] setTerrain für queryTerrainElevation fehlgeschlagen', err);
  }
}

/**
 * Chamfer sharp corners in a [lng,lat]-polyline so MapLibre's line-join
 * triangulation doesn't produce visible triangular artifacts at
 * U-turn-style switchbacks.
 *
 * Hintergrund (Wave 14aa): Der Aura-Layer unter dem ausgewählten Trail
 * zeigte an scharfen Kurven „Ohren"/Dreiecke — selbst ohne line-blur.
 * Root-Cause: MapLibre fächert bei `line-join: round` sehr scharfe Winkel
 * mit nur wenigen Triangeln auf. Das Fan-Dreiecks-Muster wird sichtbar,
 * sobald die Linie breit ist (>20 px) und der Winkel extrem (z. B.
 * Spitzkehren < 20°). Blur mildert es, erzeugt aber eigene Shader-
 * Artefakte. Die stabile Lösung ist, das Problem an der Geometrie-Quelle
 * zu beheben: scharfe Ecken werden durch einen kleinen quadratischen
 * Bezier-Arc ersetzt, sodass MapLibre gar keine harten Ecken mehr
 * triangulieren muss.
 *
 * Semantik:
 *   - Endpunkte bleiben exakt.
 *   - Innere Vertices mit Deflection < `sharpThresholdDeg` bleiben exakt
 *     (wir glätten NICHT, was eh schon gerade ist).
 *   - Bei scharfem Winkel: Pull-back um `chamferDeg` entlang v_in zu A,
 *     Pull-forward um `chamferDeg` entlang v_out zu B. Der Vertex selbst
 *     wird als Bezier-Control benutzt und in `samples` Punkten
 *     interpoliert. Chamfer wird auf 40% des kürzeren Nachbarsegments
 *     begrenzt, damit wir kurze Zacken nicht überschießen.
 *
 * Warum in Grad statt Metern: chamferDeg = 0.00007° ≈ 7–8 m bei 44°N.
 * Der Breitengrad-Unterschied zwischen Lng und Lat ist im Finale-Liguria-
 * Bereich (~44°N) knapp 30% — für eine kosmetische Glättung unter der
 * GPS-Präzision vernachlässigbar. Wir sparen uns die Mercator-Hin-und-
 * Zurück-Projektion komplett.
 *
 * Wichtig: Die ORIGINAL-Trail-Linie (aus trailLayerSpec) benutzt weiter
 * die rohen GPS-Koordinaten. Nur die Aura-Source wird chamfered — die
 * farbige Trail-Linie bleibt geografisch exakt wie aufgezeichnet. */
function chamferPolyline(
  coords: [number, number][],
  chamferDeg = 0.00007,
  sharpThresholdDeg = 20,
  hairpinThresholdDeg = 160,
  samples = 6,
): [number, number][] {
  if (coords.length < 3) return coords.map((c) => [c[0], c[1]]);

  // Wir chamferen wenn die Deflection (Abweichung von "weiter geradeaus")
  // den Schwellwert überschreitet. v_in und v_out beide "nach vorn" gerichtet:
  //   cosDef = 1  → perfekt geradeaus
  //   cosDef = 0  → 90°-Knick
  //   cosDef = -1 → 180°-Backtrack
  // Chamfer wenn cosDef < cos(threshold).
  const cosDeflectionThreshold = Math.cos((sharpThresholdDeg * Math.PI) / 180);
  // Zusätzliche Schranke: bei (fast-)180°-Kehren kollabiert das quadratische
  // Bezier A→p1→B, weil v_in und v_out anti-parallel werden — die
  // Pull-back-Punkte A und B fallen auf die gleiche Stelle (beide in
  // Einflugrichtung hinter p1) und der Bezier mit p1 als Control degeneriert
  // zu einem „Spike" nach vorn. Bei solchen Hairpins lassen wir den Vertex
  // unverändert; MapLibre's `line-join: round` erzeugt dort einen Halbkreis-
  // Fan, der zwar Triangel-facettiert ist, aber weit dezenter als ein falsch
  // gesetzter Chamfer-Spike. Zusätzlich ist bei der neuen Circle-Aura die
  // Line-Geometrie nur noch für Pfeile relevant — dort fällt ein einzelner
  // Facettenring nicht auf.
  const cosHairpinThreshold = Math.cos((hairpinThresholdDeg * Math.PI) / 180);

  const out: [number, number][] = [];
  const first = coords[0]!;
  out.push([first[0], first[1]]);

  for (let i = 1; i < coords.length - 1; i++) {
    const p0 = coords[i - 1]!;
    const p1 = coords[i]!;
    const p2 = coords[i + 1]!;
    const vInX = p1[0] - p0[0];
    const vInY = p1[1] - p0[1];
    const vOutX = p2[0] - p1[0];
    const vOutY = p2[1] - p1[1];
    const lenIn = Math.hypot(vInX, vInY);
    const lenOut = Math.hypot(vOutX, vOutY);
    if (lenIn === 0 || lenOut === 0) {
      out.push([p1[0], p1[1]]);
      continue;
    }
    const dx1 = vInX / lenIn;
    const dy1 = vInY / lenIn;
    const dx2 = vOutX / lenOut;
    const dy2 = vOutY / lenOut;
    const cosDef = dx1 * dx2 + dy1 * dy2;
    if (cosDef >= cosDeflectionThreshold) {
      // Weiche Kurve oder fast-gerade → Vertex bleibt unverändert.
      out.push([p1[0], p1[1]]);
      continue;
    }
    if (cosDef <= cosHairpinThreshold) {
      // (Fast-)180°-Kehre: Bezier würde entarten. Vertex behalten.
      out.push([p1[0], p1[1]]);
      continue;
    }
    // Scharfer (aber nicht entarteter) Knick → chamferen. Radius auf 40%
    // des kürzeren Nachbarn clampen, damit wir bei aufeinanderfolgenden
    // scharfen Zacken nicht überschießen.
    const d = Math.min(chamferDeg, 0.4 * Math.min(lenIn, lenOut));
    const ax = p1[0] - d * dx1;
    const ay = p1[1] - d * dy1;
    const bx = p1[0] + d * dx2;
    const by = p1[1] + d * dy2;
    // Quadratischer Bezier A → p1 (Kontrolle) → B, (samples+1) Punkte.
    // t=0 → A, t=1 → B. Dazwischen glatter Arc, der den harten Vertex
    // ersetzt. Anschlusssegmente bleiben gerade und treffen A bzw. B
    // tangential → glatter C¹-Übergang.
    for (let s = 0; s <= samples; s++) {
      const t = s / samples;
      const u = 1 - t;
      const x = u * u * ax + 2 * u * t * p1[0] + t * t * bx;
      const y = u * u * ay + 2 * u * t * p1[1] + t * t * by;
      out.push([x, y]);
    }
  }
  const last = coords[coords.length - 1]!;
  out.push([last[0], last[1]]);
  return out;
}

/**
 * Dense-sample eine Polyline in regelmäßigen Abständen. Wird für die
 * Circle-basierte Aura (Wave 14ab) gebraucht: statt die Spur als Line
 * zu rendern, platzieren wir an jedem dicht abgetasteten Punkt einen
 * halbdurchsichtigen Gradient-Kreis. Die Kreise überlappen sich und
 * ergeben zusammen einen weichen Schlauch um den Trail.
 *
 * Warum das wichtig ist: Ein MapLibre-`line`-Layer rendert jedes Segment
 * als ein Rechteck mit Round-Caps. Bei MTB-Switchbacks, wo zwei
 * Trail-Arme nur ein paar Meter auseinander liegen, aber die Aura ~80 m
 * breit ist, überlappen sich die Rechtecke benachbarter Arme und ihre
 * Capsule-Ränder kollidieren — sichtbar als sägezahnförmige „Zipper"-
 * Muster. Circles haben keine Capsule-Geometrie, sind rotations-
 * symmetrisch und verschmelzen organisch.
 *
 * spacingDeg ≈ 0.00008° entspricht ~9 m lat bei 44°N. Bei einem
 * 2.5-km-Trail ergibt das ~280 Punkte — für MapLibre's Circle-Layer
 * vollkommen problemlos (gerendert werden sowieso nur die sichtbaren
 * Tiles). Endpunkte sind exakt erhalten. */
function densifyPolyline(
  coords: [number, number][],
  spacingDeg = 0.00008,
): [number, number][] {
  if (coords.length < 2) return coords.map((c) => [c[0], c[1]]);
  const out: [number, number][] = [];
  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = coords[i]!;
    const p1 = coords[i + 1]!;
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const len = Math.hypot(dx, dy);
    // Mindestens ein Sample pro Segment (Start-Punkt), sonst ceil(len/spacing)
    // Samples gleichmäßig verteilt. Wir pushen nur s=0..segments-1 — der
    // End-Punkt wird vom nächsten Segment als Start wieder aufgenommen, damit
    // keine Duplikate entstehen.
    const segments = Math.max(1, Math.ceil(len / spacingDeg));
    for (let s = 0; s < segments; s++) {
      const t = s / segments;
      out.push([p0[0] + t * dx, p0[1] + t * dy]);
    }
  }
  // Exakter Endpunkt: wurde von der Schleife oben weggelassen, damit die
  // Start/End-Marker trotzdem exakt auf dem letzten GPS-Punkt sitzen.
  const last = coords[coords.length - 1]!;
  out.push([last[0], last[1]]);
  return out;
}

// Modus → Style-Objekt. Wird sowohl beim Initial-Mount als auch beim Live-Switch
// via map.setStyle() benutzt. Out-of-component damit keine wandernden Closures
// in Effekt-Deps stecken.
function buildStyleForMode(mode: FallbackStyleName, urls: ManifestUrls) {
  const { mapUrl, contourUrl, hillshadeUrl } = urls;
  if (mapUrl && mode === 'mapout') {
    // Wave 14ce: Hillshade-Layer IMMER ins Style einbauen — Sichtbarkeit
    // wird live über setLayoutProperty('hillshade','visibility',...) in
    // MapPage gesteuert (mode/compassMode/showHillshade-Pref).
    //
    // Vorher (Wave 14bd) wurde der Layer nur erzeugt wenn lokale Hillshade-
    // PMTiles im OPFS lagen. Ohne lokale Datei → kein Layer → setLayout-
    // Property ein No-Op → User sah nie hillshade obwohl in Profile aktiv.
    // User-Bug-Report 14ce: "man sieht hillshade nicht, auch wenn es
    // enabled ist". Trade-off: ohne lokale PMTiles fällt style.ts auf AWS-
    // Terrarium-DEM zurück; offline + keine lokale Datei = Hillshade
    // rendert leer. Graceful Degradation, nicht crashy.
    return buildLiguriaStyle({
      mapPmtilesUrl: mapUrl,
      contourPmtilesUrl: contourUrl,
      hillshade: true,
      hillshadePmtilesUrl: hillshadeUrl,
    });
  }
  if (mapUrl && mode === 'minimal') {
    return buildMinimalLiguriaStyle({ mapPmtilesUrl: mapUrl });
  }
  if (mapUrl && mode === 'outdoor') {
    // Outdoor = mapout ohne Hillshade, mit Contours (z.B. für Low-Power-Modus)
    return buildLiguriaStyle({
      mapPmtilesUrl: mapUrl,
      contourPmtilesUrl: contourUrl,
      hillshade: false,
    });
  }
  if (mode === 'mapout') {
    return buildMapoutStyle();
  }
  // 'satellite', 'minimal', 'outdoor' ohne PMTiles → Raster-Fallback
  return buildFallbackRasterStyle(mode);
}

export function MapView({
  trails = [],
  onTrailClick,
  selectedTrailId = null,
  selectionOverride = null,
  rideNextAuraMeters = 0,
  fallbackStyle = 'mapout',
}: MapViewProps) {
  // Wave 14ca-fix64: Color-Assist-Modus. Wert hier halten damit der
  // trails-Effect bei Toggle-Aenderung re-runned und die Feature-Properties
  // (lineColor, labelColor) mit der neuen Palette neu gebaut werden.
  const [colorAssistMode] = useColorAssistMode();
  // Wave 14cf: Active-Region steuert Initial-Camera (Liguria vs Elba) UND
  // welches PMTiles-File aus dem multi-region-Manifest als Map gewählt
  // wird. Ein Region-Wechsel triggert einen Map-Rebuild via rebuildNonce
  // (Effekt unten).
  const [activeRegion] = useActiveRegion();
  // Overlays (Route, Korridor-Umriss, Routing-BEREICHE) eines selbst
  // gebauten Korridors. 3PBR bringt sie im Bundle mit; für dynamische
  // Slugs holt sie corridorOverlays vom Server und cached sie in IndexedDB.
  const [corridorOverlays, setCorridorOverlays] = useState<RegionMapData | null>(null);
  useEffect(() => {
    let alive = true;
    // Wave 14ed: '3pbr' ist keine eingebaute Region mehr — der Sonderfall
    // faellt weg. Ohne aktive Region gibt es schlicht keine Overlays.
    if (!activeRegion) {
      setCorridorOverlays(null);
      return;
    }
    setCorridorOverlays(null);
    void getCorridorOverlays(activeRegion, (fresh) => {
      if (alive) setCorridorOverlays(fresh);
    }).then((ov) => {
      if (alive && ov) setCorridorOverlays(ov);
    });
    return () => {
      alive = false;
    };
  }, [activeRegion]);
  // Wave 14cf: GPS-Position für die Ride-Next-Aura. Hook abonniert positionBus
  // und liefert bei jedem Tick neue Coords (oder null wenn Geolocation aus).
  const currentPosition = useCurrentPosition();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  // Wave 14cf-fix5: Hint für monotone Progression der Ride-Next-Aura.
  // Wird nach jedem GPS-Tick aktualisiert und beim Track-Wechsel zurückgesetzt.
  const rideNextHintRef = useRef<{ trackKey: string; meters: number | null }>({
    trackKey: '',
    meters: null,
  });
  // Manifest-URLs werden nach dem ersten Fetch gespeichert, damit der
  // Switch-Effect (siehe unten) auch ohne erneuten Backend-Roundtrip
  // einen neuen Style bauen kann.
  const manifestUrlsRef = useRef<ManifestUrls | null>(null);
  // Wave 14be: onTrailClick in einem Ref halten. Der Click-Handler auf der
  // 'trails-hit'-Layer wird nur EINMAL gebunden (siehe Effect weiter unten);
  // aktuelle Callback-Identity wird via Ref gelesen. So flackert der Listener
  // nicht bei jedem MapPage-Re-Render, und der Trail-Render-Effect hängt nicht
  // mehr von `onTrailClick` als Dependency ab.
  const onTrailClickRef = useRef(onTrailClick);
  useEffect(() => {
    onTrailClickRef.current = onTrailClick;
  }, [onTrailClick]);
  // Signalisiert dass die Map initialisiert UND Style geladen ist.
  // Der Trail-useEffect hängt davon ab — sonst läuft er auf einer leeren Map.
  const [mapReady, setMapReady] = useState(false);
  // Bump zwingt einen kompletten Map-Rebuild (neue MapLibre-Instanz). Wird hochgezählt
  // wenn iOS Safari den WebGL-Kontext dauerhaft verliert und nicht restored. Trigger
  // für den Mount-Effect via Dependency.
  const [rebuildNonce, setRebuildNonce] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    // Wenn ein alter Map-Instance noch lebt (z.B. nach einem Rebuild-Trigger):
    // erst aufräumen, dann neu bauen. Ohne das hätten wir zwei Canvases überlagert.
    if (mapRef.current) {
      try { mapRef.current.remove(); } catch { /* ignore */ }
      mapRef.current = null;
    }
    console.info('[map] mount', { rebuildNonce, fallbackStyle });
    registerPmtilesProtocol();

    let aborted = false;
    (async () => {
      const manifest = await tilesManifest();
      if (aborted) return;

      // Wave 14cf: aus dem Manifest die Files für die aktive Region picken.
      // Wenn der Backend ein neues regions[]-Manifest liefert, nehmen wir
      // den passenden Eintrag; sonst (alter Backend) den flachen Top-Level-
      // Slot, der per Konvention auf Liguria zeigt. Falls die Active-Region
      // im Manifest nicht existiert (User hat Elba aktiv gewählt aber das
      // Backend kennt es noch nicht), fallen wir auf den ersten Eintrag
      // bzw. die Top-Level-Slots zurück.
      const regionEntry = manifest.regions?.find((r) => r.id === activeRegion)
        ?? manifest.regions?.[0]
        ?? null;
      // ULTRA: NUR die Tiles der aktiven Region nutzen — KEIN Fallback mehr auf
      // die alten Env-Top-Level-Slots (zeigten auf liguria.pmtiles, ein Rudiment
      // der MTB-App). Solange die Korridor-Tiles (3pbr.*) noch nicht gebaut sind
      // (Phase 2c), bleiben diese null → buildStyleForMode fällt automatisch auf
      // die ONLINE-Basemap zurück (OpenFreeMap Liberty bzw. Raster), die ganz
      // Europa abdeckt. Sobald 3pbr.pmtiles existiert, greift wieder offline.
      const mapEntry = regionEntry?.map ?? null;
      const contoursEntry = regionEntry?.contours ?? null;
      const hillshadeEntry = regionEntry?.hillshade ?? null;

      // Debug-Escape-Hatch: '?nopmtiles=1' in der URL überspringt PMTiles
      // und zwingt den Online-Fallback. Nützlich um zu isolieren ob
      // das Problem in den PMTiles liegt oder im restlichen Style.
      const skipPmtiles = new URLSearchParams(location.search).has('nopmtiles');

      // Wave 14bd: Hillshade nur einbinden wenn die Datei auch tatsächlich
      // im OPFS liegt. FirstLaunchGate lädt sie seit 14bd bewusst NICHT mehr
      // automatisch runter (User-Wunsch: offline ohne 3D-Relief). Wenn der
      // User sie auf der OfflinePage manuell runterlädt, greift sie hier.
      // Ohne Datei → gar keine hillshadeUrl → style.ts rendert keinen
      // Hillshade-Layer (kein AWS-Fallback-Flicker).
      const hillshadeAvailable =
        !skipPmtiles && hillshadeEntry
          ? (await opfsStatus(`${location.origin}${hillshadeEntry.url}`)).exists
          : false;
      if (aborted) return;

      // PMTiles-URL-Form: pmtiles://{http-url}
      const urls: ManifestUrls = {
        mapUrl: !skipPmtiles && mapEntry
          ? `pmtiles://${location.origin}${mapEntry.url}`
          : '',
        contourUrl: !skipPmtiles && contoursEntry
          ? `pmtiles://${location.origin}${contoursEntry.url}`
          : undefined,
        hillshadeUrl: hillshadeAvailable && hillshadeEntry
          ? `pmtiles://${location.origin}${hillshadeEntry.url}`
          : undefined,
      };
      manifestUrlsRef.current = urls;
      console.info('[map] manifest resolved', {
        map: !!urls.mapUrl,
        contours: !!urls.contourUrl,
        hillshade: !!urls.hillshadeUrl,
      });

      // Wave 14bd: PMTiles-Archives mit OPFS-Source vorregistrieren. Jeder
      // Tile-Read wird ein File.slice + arrayBuffer — disk-backed, keine
      // volle Datei im RAM, keine HTTP-Semantik, keine Workbox in der Nähe.
      //
      // Key MUSS exakt die URL sein die nach 'pmtiles://' kommt (Protocol
      // matcht per strictem String-Gleichheit). Deshalb den Prefix wegstrippen.
      if (!skipPmtiles) {
        if (urls.mapUrl) addPmtilesArchive(urls.mapUrl.slice('pmtiles://'.length));
        if (urls.contourUrl) addPmtilesArchive(urls.contourUrl.slice('pmtiles://'.length));
        if (urls.hillshadeUrl) addPmtilesArchive(urls.hillshadeUrl.slice('pmtiles://'.length));

        // Prewarm: File-Handle (nicht-blob) vor erstem Tile-Request holen.
        // Bei OPFS ist das billig — nur getFileHandle + getFile, ohne
        // Inhalt zu laden. Non-blocking.
        void prewarmPmtilesArchives();
      }

      // Style-Auswahl — der fallbackStyle-Param wirkt immer (nicht nur wenn
      // PMTiles fehlen). Bei vorhandenen PMTiles gibt's drei Varianten:
      //   - mapout:    reicher Topo-Look mit Contours + Hillshade (3D-Eindruck)
      //   - minimal:   clean Trails-First, keine Contours, monochrome Straßen
      //   - satellite: Online-Satellit (umgeht PMTiles komplett)
      // Ohne PMTiles: Online-Fallbacks (mapout→OFM Liberty, sonst Raster).
      let style;
      try {
        style = buildStyleForMode(fallbackStyle, urls);
      } catch (err) {
        console.error('[map] Style-Build fehlgeschlagen, fallback auf minimal:', err);
        style = buildFallbackRasterStyle('minimal');
      }

      if (!urls.mapUrl) {
        console.info(
          `[map] Fallback (${fallbackStyle}). Für Offline-Modus: scripts/generate-pmtiles.sh ausführen.`,
        );
      }

      // Wave 14cf: Initial-Camera-Center kommt aus shared/REGIONS, je nachdem
      // welche Region der User gewählt hat. Wenn der Manifest-Eintrag eigene
      // Bounds liefert (Backend ist autoritativ), nehmen wir die — sonst die
      // shared-Defaults. Falls die Active-Region in keiner der beiden Quellen
      // bekannt ist, fällt es auf Liguria zurück.
      const regionCenter: [number, number] =
        regionEntry?.center ??
        (REGIONS[activeRegion]?.center) ??
        CORRIDOR_CENTER;

      const map = new maplibregl.Map({
        container: containerRef.current!,
        style: style as any,
        center: regionCenter,
        zoom: 10,
        minZoom: 7,
        // User-Wunsch Batch 7: "viel mehr reinzoomen können auf Wunsch, mind
        // bis Level 20, Trailfork hat es auch." MapLibre overzoomt automatisch
        // aus den höchsten verfügbaren Tiles — für Vector-Sources (MapOut /
        // OpenFreeMap Liberty, contours, Trails-GeoJSON) bleibt alles crisp
        // weil Vektor-Daten beliebig skalieren. Für Raster-Fallbacks
        // (minimal z19, satellite z19, outdoor z18) wird's beim Überschreiten
        // der native-zoom entsprechend pixelig — exakt wie bei Trailfork, der
        // User akzeptiert das bewusst um sehr nahe rangehen zu können
        // (GPS-Fine-Targeting, Single-Track-Verfolgung auf engen Kurven).
        maxZoom: 20,
        // User-Feedback 14d: "das 'i' für die karteninfos unten links statt
        // rechts, kleiner machen". Wir disablen hier die default-Position
        // (bottom-right) und hängen die Attribution gleich nach Map-Create
        // explizit an 'bottom-left' — das gibt dem rechten Rand komplett der
        // LocateControl + CompassRose.
        attributionControl: false,
        hash: false,
        // Wave 14bh — Akku-Optimierung: DPR-Clamp auf 1.5.
        //
        // Hintergrund: iPhone 17 Pro Max meldet DPR=3 → MapLibre rendert
        // native auf 3× Pixeln pro CSS-Pixel, d.h. 9× Fill-Rate gegenüber
        // DPR=1. Für einen Vollbild-Map-Canvas (~400×800 CSS-Pixel) sind
        // das ~960k vs ~8.6M native Pixel pro Frame — und bei Kompass-
        // Heading-Rotation rendern wir 15 FPS kontinuierlich. Der GPU-
        // Durchsatz driftet damit klar über den mobile Energy-Budget-
        // Threshold.
        //
        // Clamp auf 1.5 (≈1080p-Äquiv. auf 400×800): 2.25M native Pixel
        // pro Frame → ~4× Ersparnis. Visuell: Schriften und Linien werden
        // minimal "weicher", aber für die Map-Ansicht im Feld kaum
        // wahrnehmbar. User hat bewusst diesen aggressiveren Wert gewählt
        // (statt meines vorgeschlagenen 2.0).
        //
        // Der pixelRatio: 2 auf map.addImage unten (Trail/Crux-Icons) ist
        // davon unberührt — das ist die Icon-SDF-Auflösung, nicht der
        // Canvas-Render-DPR.
        pixelRatio: 1.5,
      });
      mapRef.current = map;
      // Konsumenten außerhalb von MapView (z. B. ElevationBadge) zugänglich machen.
      mapBus.set(map);

      // Wave 14ce: Attribution unten, immer voll sichtbar (kein Compact-i).
      // User-Wunsch: "es soll immer unten sein so durchsichtig wie möglich
      // aber dass man es noch lesen kann, immer wenn man eine karte sieht,
      // muss man auch das sehen." compact:false rendert die Attribution
      // als Inline-Text-Strip (kein Click-to-Expand), bottom-left positioniert
      // ihn über der Tab-Bar und unter der BottomBar; styles/index.css gibt
      // ihm die transparente Optik (rgba weiß auf transparentem Hintergrund).
      map.addControl(
        new maplibregl.AttributionControl({ compact: false }),
        'bottom-left',
      );

      // MapLibre-Fehler (Tile-Loads, Style-Parse, Source-Resolve) ins Log,
      // damit wir bei schwarzem Screen was zum Diagnostizieren haben.
      // Wir loggen möglichst viel Kontext: welche Source/URL, welcher Tile,
      // welcher Layer. "Unimplemented type: 4" in pbf.skip() bedeutet praktisch
      // immer: der Parser bekommt was Nicht-Protobuf (HTML, gzipped-doppelt,
      // abgeschnittene Range) und versucht es als MVT/Glyph-PBF zu lesen.
      map.on('error', (e: any) => {
        const err = e.error;
        const ctx: Record<string, unknown> = {
          message: err?.message || String(err),
          // MapLibre hängt je nach Fehler-Herkunft verschiedene Felder dran:
          sourceId: e.sourceId,
          tile: e.tile ? {
            z: e.tile.tileID?.canonical?.z,
            x: e.tile.tileID?.canonical?.x,
            y: e.tile.tileID?.canonical?.y,
            state: e.tile.state,
          } : undefined,
          source: e.source,
          dataType: e.dataType,
          // URL-Hinweise aus dem Fehler-Objekt ziehen (je nach MapLibre-Version)
          url: err?.url || e.url,
          status: err?.status,
        };
        // Unknown-Wire-Type-Fehler besonders markieren — das ist die
        // typische "Response ist kein Protobuf"-Signatur.
        if (/Unimplemented type/.test(ctx.message as string)) {
          console.error('[map] PBF parse failed (wahrscheinlich: Tile-Response ist kein MVT/Glyph-PBF)', ctx, err);
        } else {
          console.error('[map] runtime error:', ctx, err);
        }
      });

      // KEIN NavigationControl mehr (+/-, Kompass-Reset, 3D) — User pinch-zoomt,
      // und die entfernten Buttons brauchen wir nicht. Nur 2 Buttons:
      //  1. LocateControl (auto-startet GPS-Tracking beim App-Start)
      //  2. CompassRose (Dreifach-Rolle, Batch 7: zeigt Norden UND zyklt per
      //     Tap durch 3 Modi north → free → heading → north. 'free' erlaubt
      //     2-Finger-Rotation, s. CompassRose.ts)
      // Reihenfolge = vertikale Stapelung in 'top-right'.
      // LocateControl als Instanz-Variable halten und an den Bus publizieren —
      // MapPage bedient sie imperativ (Ride-Button forceActivate, lockZoom-Sync
      // aus Settings). Ohne Bus müsste das durch die React-Tree prop-drillen.
      //
      // WICHTIG: initiale lockZoom vom gespeicherten Explore-Zoom ableiten.
      // Wenn wir das auslassen, fällt LocateControl im ersten flyTo auf
      // map.getMaxZoom() (= 20 / 5-m-Scale) zurück, und der User sieht beim
      // App-Start für 800 ms einen Mega-Zoom bevor der MapPage-useEffect
      // setLockZoom(exploreZoom) nachzieht. User-Wunsch Batch 8: "wenn ich
      // die app starte geht die zoomstufe auf max (5m skala), das ist quatsch,
      // sie soll bitte auf die stufe von 'explore zoom' gehen". Wir lesen hier
      // direkt aus localStorage (nicht via Hook), weil MapView beim Mount
      // sync initialisieren muss, bevor autoStart feuert.
      const storedExploreZoom = (() => {
        try {
          const raw = localStorage.getItem(PREF_EXPLORE_ZOOM);
          if (raw == null) return EXPLORE_ZOOM_DEFAULT;
          const n = Number.parseFloat(raw);
          return Number.isFinite(n) ? n : EXPLORE_ZOOM_DEFAULT;
        } catch {
          return EXPLORE_ZOOM_DEFAULT;
        }
      })();
      const locate = new LocateControl({
        autoStart: true,
        lockZoom: storedExploreZoom,
      });
      map.addControl(locate, 'top-right');
      locateControlBus.set(locate);

      // Compass-Rose ebenfalls an Bus — MapPage schaltet sie im Ride-Mode
      // automatisch auf 'heading' (User-Wunsch).
      const compass = new CompassRose();
      map.addControl(compass, 'top-right');
      compassBus.set(compass);

      // User-Rotation der Karte wird jetzt (Batch 7) von der CompassRose pro
      // Modus verwaltet: north/heading → disabled, free → enabled. Hier also
      // nichts mehr hart sperren — der CompassRose-onAdd() ruft am Ende
      // _applyRotationLock() und setzt den richtigen Initialzustand (north →
      // disabled). Ohne diesen Delegations-Pfad hatten wir vorher NIE freie
      // Rotation, was User-Wunsch Batch 7 war ("mit 2 Fingern rotieren").

      // MapLibre's native ScaleControl wurde durch unsere eigene <ScaleBar/>
      // (components/ScaleBar.tsx) ersetzt — sie ist Teil der vereinheitlichten
      // Bottom-Pill und teilt den Turbo-Design-Look mit der TabBar. Das
      // Maplibre-Native-Control hat ein weißes Chrome das sich nicht sauber
      // in unsere dunkle Pill einfügt.

      // 'load' feuert wenn Style + alle initialen Tiles geladen sind.
      // Ab dann sind getSource/addSource/addLayer sicher benutzbar.
      map.on('load', () => {
        if (!aborted) {
          console.info('[map] style loaded');
          // urls werden gebraucht um einen DEM-Source nachzuziehen wenn der
          // Style keinen mitbringt (minimal/outdoor). Ohne die URLs würde
          // queryTerrainElevation für diese Styles weiterhin nix liefern.
          enableSilentTerrain(map, manifestUrlsRef.current);
          setMapReady(true);

          // Wave 14bb: forced repaint. MapLibre's initialer Tile-Batch feuert
          // manchmal zu früh (bevor PMTiles-Blob im Memory ist) und sitzt dann
          // auf "waiting" — besonders auf iOS Safari nach einem Refresh.
          // triggerRepaint() erzwingt einen frischen Render-Pass für den
          // aktuellen Viewport; Tiles die noch nicht da sind werden neu
          // angefragt, und die Blob-Reads sind nach prewarmPmtilesArchives()
          // instant.
          try {
            map.triggerRepaint();
          } catch { /* ignore */ }
        }
      });
      // Bei Style-Wechsel (setStyle) einmal re-applyen — Sources werden
      // komplett neu gebaut, also ist unser vorheriger setTerrain-Call weg.
      map.on('style.load', () => {
        enableSilentTerrain(map, manifestUrlsRef.current);
      });
    })();

    return () => {
      aborted = true;
      console.info('[map] unmount → destroying MapLibre instance');
      mapRef.current?.remove();
      mapRef.current = null;
      mapBus.set(null);
      locateControlBus.set(null);
      compassBus.set(null);
      setMapReady(false);
    };
    // Wave 14cf: activeRegion in den Deps damit ein Region-Wechsel im
    // OfflinePage einen Map-Rebuild triggert — neue PMTiles-Source +
    // neue Initial-Camera. Ohne diese Dep würde der User nach Klick
    // auf "Elba" weiterhin auf Liguria-Karten schauen bis er reloaded.
  }, [rebuildNonce, activeRegion]);

  // iOS-Safari-Härtung gegen verdeckten Canvas.
  //
  // Symptom: nach Tab-Switch (Overlay-Seite liegt drüber) und Zurückwechseln
  // rendert der Map-Canvas nicht mehr, obwohl die MapLibre-Instanz noch lebt.
  // Drei mögliche Ursachen auf iOS Safari PWA:
  //   1) iOS Safari pausiert den WebGL-Kontext wenn der Canvas unsichtbar ist
  //      → 'webglcontextlost' event, MapLibre verliert alle GPU-Ressourcen.
  //   2) document.hidden → Safari "friert" den Canvas ein.
  //   3) BFCache-Restore (pageshow persisted=true) lässt den Canvas ohne
  //      Neu-Paint zurück.
  //
  // Defense-in-Depth:
  //   - visibilitychange / pageshow: map.resize() + triggerRepaint() (günstig,
  //     falls Kontext noch da ist)
  //   - webglcontextrestored: dasselbe, falls Kontext restored wird
  //   - webglcontextlost: volles Rebuild via rebuildNonce-State-Bump (letzter
  //     Ausweg — MapLibre's context-loss-handling ist nicht immer zuverlässig
  //     auf iOS Safari).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const canvas = map.getCanvas();

    const forceRepaint = (reason: string) => {
      const m = mapRef.current;
      if (!m) return;
      console.info('[map] forceRepaint:', reason);
      try {
        m.resize();
        m.triggerRepaint();
      } catch (err) {
        console.warn('[map] forceRepaint failed', err);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        forceRepaint('visibilitychange→visible');
      }
    };
    const onPageShow = (e: PageTransitionEvent) => {
      forceRepaint(`pageshow(persisted=${e.persisted})`);
    };
    const onContextLost = (e: Event) => {
      // Default-Action ist "Kontext endgültig verloren" — preventDefault()
      // signalisiert dass wir restore wollen. Falls restore nicht innerhalb
      // weniger Frames kommt, bauen wir per Nonce komplett neu.
      e.preventDefault();
      console.warn('[map] webglcontextlost — warte auf restore, sonst Rebuild');
      const timer = window.setTimeout(() => {
        console.warn('[map] webglcontext nicht restored → Rebuild');
        setRebuildNonce((n) => n + 1);
      }, 1500);
      canvas.addEventListener(
        'webglcontextrestored',
        () => window.clearTimeout(timer),
        { once: true },
      );
    };
    const onContextRestored = () => {
      console.info('[map] webglcontextrestored');
      forceRepaint('webglcontextrestored');
    };

    // Custom Event von App.tsx: feuert wenn der Karten-Canvas nach einer
    // Overlay-Route wieder sichtbar wird. Siehe App.tsx zur Herkunft.
    const onMapRevealed = () => forceRepaint('adt:map-revealed');

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('adt:map-revealed', onMapRevealed);
    canvas.addEventListener('webglcontextlost', onContextLost as EventListener);
    canvas.addEventListener('webglcontextrestored', onContextRestored);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('adt:map-revealed', onMapRevealed);
      canvas.removeEventListener('webglcontextlost', onContextLost as EventListener);
      canvas.removeEventListener('webglcontextrestored', onContextRestored);
    };
  // mapReady im Dep-Array: Handler müssen nach jedem map-Rebuild neu an den
  // FRISCHEN Canvas gebunden werden (getCanvas() gibt den alten zurück wenn
  // wir zu früh bind'en).
  }, [mapReady, rebuildNonce]);

  // Live-Style-Switch ohne Remount: die Map bleibt, nur der Style wird getauscht.
  // MapLibre's setStyle() behält Kamera-State (Zentrum, Zoom, Pitch, Bearing),
  // daher kann man zwischen Mapout/Minimal/Satellit wechseln ohne rauszuspringen.
  //
  // Tradeoff: setStyle entfernt auch unsere Trail-Sources → wir flippen mapReady
  // kurz auf false und warten auf 'style.load', dann zieht der Trail-Effect
  // unten automatisch nach und fügt die Trails neu hinzu.
  //
  // Wave 14aw (kritischer Fix): Wenn 'style.load' offline NICHT feuert (z. B.
  // weil Workbox einen Glyph-/Tile-Request abbricht oder ein asynchroner
  // Sprite-Fetch stecken bleibt), blieb `mapReady` für immer auf false — der
  // User sah nach dem Style-Switch eine leere Karte ohne Trails UND konnte
  // durch Zurückwechseln NICHTS wiederherstellen, weil auch der Rückschwenk
  // wieder auf einem hängenden style.load wartete. Robustheit-Layer:
  //   1) 'style.load' als Primär-Signal (feuert normal nach wenigen ms)
  //   2) 'idle' als Backup (feuert sobald Tiles + Kamera still stehen —
  //      zuverlässiger als style.load bei teilweise-offline-Situation)
  //   3) 4-Sek-Timeout als HARD-Fallback: egal was passiert, mapReady geht
  //      wieder auf true; nachgelagerte Effects (Trails, Selection) bauen
  //      ihre Sourcen sowieso idempotent auf (siehe Trail-useEffect unten).
  // Alle drei Pfade sind idempotent gegeneinander — wer zuerst kommt,
  // gewinnt, die anderen no-op'n.
  const lastModeRef = useRef<FallbackStyleName>(fallbackStyle);
  useEffect(() => {
    const map = mapRef.current;
    const urls = manifestUrlsRef.current;
    if (!map || !urls) return;
    // Beim Initial-Mount fällt fallbackStyle === lastModeRef.current — der
    // Mount-Effect hat den Style schon korrekt gebaut, hier gibt's nichts zu tun.
    if (lastModeRef.current === fallbackStyle) return;

    // Wave 14aw: Satellite offline sauber abblocken. Satellite fetcht von
    // arcgisonline.com (externes CDN, keine PMTiles), d. h. ohne Netz zeigt
    // die Karte nur den dunkelblauen Background (#1a2030). Das ist schlimmer
    // als einfach im aktuellen Style bleiben — der User weiss nicht ob die
    // App kaputt ist oder nur offline. Wir cancellen den Switch lautlos, der
    // MapPage-useEffect weiter oben hat den Button für Satellite offline
    // bereits ausgeblendet; dieser Guard greift falls der Style vor dem
    // Online→Offline-Übergang gesetzt war.
    if (fallbackStyle === 'satellite' && !navigator.onLine) {
      console.warn('[map] Satellite offline abgelehnt — bleibe auf', lastModeRef.current);
      return;
    }

    lastModeRef.current = fallbackStyle;

    let newStyle;
    try {
      newStyle = buildStyleForMode(fallbackStyle, urls);
    } catch (err) {
      console.error('[map] Style-Switch fehlgeschlagen:', err);
      return;
    }

    // mapReady=false sperrt den Trail-Effect bis die neuen Sources wieder da sind
    setMapReady(false);
    // diff: false zwingt komplettes Neuladen — bei sehr unterschiedlichen Styles
    // (Mapout ↔ Satellit) bringt diff nichts außer subtile Bugs.
    //
    // Wave 14ca-fix64-v2: transformStyle preserved die Trails-Source +
    // die 7 trailLayerSpec-Layer across den Style-Switch. Damit fluktuieren
    // die Trails NICHT mehr 4-5 Sekunden, waehrend MapLibre den neuen
    // Style komplett laedt — sie sind im Moment des setStyle bereits Teil
    // des neuen Styles und werden ohne Flicker durchgereicht.
    //
    // Conservative scope: nur `trails`-Source + Layer mit `id`
    // beginnend mit 'trails-'. Andere custom Sources/Layers (POIs,
    // Selection-Overlay, Endpoints-All, Routen-Highlights) reattachen
    // ueber ihre eigenen Effects sobald mapReady=true ist — die paar
    // hundert Millisekunden Flicker dort sind weniger schmerzhaft als
    // die Trails-Linien selbst.
    try {
      map.setStyle(newStyle as any, {
        diff: false,
        transformStyle: (prev, next) => {
          if (!prev) return next;
          const prevSources = (prev.sources ?? {}) as Record<string, any>;
          const prevLayers = (prev.layers ?? []) as Array<{ id: string }>;
          const nextSources = (next.sources ?? {}) as Record<string, any>;
          const nextLayerIds = new Set(
            ((next.layers ?? []) as Array<{ id: string }>).map((l) => l.id),
          );
          // Custom-Source-IDs erkennen wir am Prefix oder bekannten Namen.
          // Alle anderen kommen aus dem Base-Style (osm, terrain etc.) und
          // werden vom neuen Style frisch geliefert.
          const isCustomSource = (id: string) =>
            id === TRAILS_SOURCE_NAME ||
            id.startsWith('trail-') ||
            id.startsWith('trails-') ||
            id.startsWith('adt-');
          const isCustomLayer = (id: string) =>
            id.startsWith('trail-') ||
            id.startsWith('trails-') ||
            id.startsWith('adt-');
          const preservedSources: Record<string, any> = {};
          for (const [id, src] of Object.entries(prevSources)) {
            if (isCustomSource(id) && !(id in nextSources)) {
              preservedSources[id] = src;
            }
          }
          // Layer-Reihenfolge des Vorgaengers beibehalten; an die Liste
          // des neuen Styles HINTEN anhaengen (also OBEN auf der Karte —
          // genau dort wollen wir die Trails/Overlays).
          const preservedLayers = prevLayers.filter(
            (l) =>
              typeof l.id === 'string' &&
              isCustomLayer(l.id) &&
              !nextLayerIds.has(l.id),
          );
          return {
            ...next,
            sources: {
              ...nextSources,
              ...preservedSources,
            },
            layers: [
              ...((next.layers ?? []) as any[]),
              ...preservedLayers,
            ],
          };
        },
      });
    } catch (err) {
      console.error('[map] map.setStyle wirft — forciere mapReady=true:', err);
      setMapReady(true);
      return;
    }

    // Wave 14ca-fix64-v2: Ready-Signal aggressiv ueber drei Quellen.
    //
    // User-Bug: "ich wechsle die karte und die trails verschwinden, das
    // dauert 4-5 Sekunden bis sie wieder kommen". Die 4-5s waren der
    // urspruengliche timeout-4s, weil weder style.load noch idle bei
    // diff:false zuverlaessig promptly gefeuert haben.
    //
    // Strategie:
    //   1. style.load + styledata + idle ALLE binden (mit `on`, nicht
    //      `once`). styledata feuert mehrfach waehrend des Style-Aufbaus
    //      und ist meistens das schnellste Ready-Signal.
    //   2. In jedem Callback `isStyleLoaded()` checken — sobald true,
    //      setMapReady(true) und alle Listener abraeumen.
    //   3. 4s-Timeout als Notbremse (nicht 8s — wir wollen NICHT, dass
    //      der User auf einen versehentlichen Edge-Case 8s wartet).
    //   4. Trails-Effect hat schon einen eigenen Retry-Loop bei
    //      addSource/addLayer-Fail (siehe scheduleRetry unten), das
    //      faengt eventuelle Race-Conditions wenn mapReady kurz vor
    //      isStyleLoaded() flippt.
    let settled = false;
    let cleanupReadyListeners: (() => void) | null = null;
    const markReady = (via: string) => {
      if (settled) return;
      // Style muss tatsaechlich geladen sein. Kein blindes flippen — wir
      // warten auf den naechsten Event falls noch nicht so weit.
      if (!map.isStyleLoaded()) return;
      settled = true;
      console.info(`[map] Style-Switch fertig via ${via} (→ mapReady=true)`);
      cleanupReadyListeners?.();
      window.clearTimeout(timeoutHandle);
      setMapReady(true);
    };
    const onStyleLoad = () => markReady('style.load');
    const onStyleData = () => markReady('styledata');
    const onIdle = () => markReady('idle');
    map.on('style.load', onStyleLoad);
    map.on('styledata', onStyleData);
    map.on('idle', onIdle);
    cleanupReadyListeners = () => {
      map.off('style.load', onStyleLoad);
      map.off('styledata', onStyleData);
      map.off('idle', onIdle);
    };
    const timeoutHandle = window.setTimeout(() => {
      // Notbremse: nach 4s forcieren wir mapReady=true auch wenn keiner der
      // Events durchgekommen ist. Trails-Effect retried selbststaendig
      // wenn addSource/addLayer noch wirft.
      if (settled) return;
      settled = true;
      console.warn('[map] Style-Switch timeout (4s) — forciere mapReady=true');
      cleanupReadyListeners?.();
      setMapReady(true);
    }, 4000);

    return () => {
      // Effect wird erneut getriggert (z. B. noch ein Style-Klick bevor der
      // erste Switch fertig war) → aktuellen Warte-Loop abräumen, der
      // nächste Run baut einen neuen.
      cleanupReadyListeners?.();
      window.clearTimeout(timeoutHandle);
    };
  }, [fallbackStyle]);

  // Trails hinzufügen / updaten — Wave 14be: SINGLE-SOURCE-REFACTOR.
  //
  // Vorher (bis 14bd): pro Trail eine eigene GeoJSON-Source + 5–6 Layer.
  // Bei 28 Trails = 28 Sources + ~140 Layer. Bei 200 Trails: Katastrophe.
  // Plus: pro Trail ein eigener `map.on('click', ...)`, der beim Umbauen
  // NICHT abgeräumt wurde → Listener-Leak bei jedem Trail-Wechsel.
  //
  // Jetzt (14be): EINE Source `trails` mit FeatureCollection über ALLE
  // Trails, plus FIXE 7 Layer (trailLayerSpec returns sie global). Styling
  // pro Trail läuft über Feature-Properties (resolveTrailStyle baut sie);
  // die Layer lesen via `['get', 'lineColor']` etc. Bei Updates: nur ein
  // `setData` auf der einen Source — MapLibre bearbeitet einen einzigen
  // GeoJSON-Layer-Graph statt 28.
  //
  // Click/Hover-Listener werden in einem separaten Effect unten EINMAL auf
  // 'trails-hit' gebunden und lesen onTrailClick via Ref — hängt also nicht
  // mehr an der Dependency-Liste dieses Effects.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const apply = () => {
      // Jedes Feature kriegt seine Style-Info als Property (von MapLibre's
      // `['get', '…']`-Expressions in trailLayerSpec gelesen). resolveTrailStyle
      // zentralisiert die Sonderfälle extreme/intermediate_plus — kein
      // Style-Branching mehr im Layer-Spec.
      const features: GeoJSON.Feature<GeoJSON.LineString>[] = trails.map((t) => {
        const geom = trackToGeoJson(t.track);
        const resolved = resolveTrailStyle(t.difficulty, t.colorHex);
        return {
          type: 'Feature',
          geometry: geom.geometry,
          properties: {
            id: t.id,
            name: (geom.properties as { name?: string } | null)?.name ?? '',
            lineColor: resolved.lineColor,
            labelColor: resolved.labelColor,
            isDashed: resolved.isDashed,
            hasUndercoat: resolved.hasUndercoat,
          },
        };
      });
      const fc: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
        type: 'FeatureCollection',
        features,
      };

      const existing = map.getSource(TRAILS_SOURCE_NAME) as
        | maplibregl.GeoJSONSource
        | undefined;
      if (existing) {
        existing.setData(fc);
        return;
      }

      // Erster Build (oder nach setStyle, das alle Sources/Layer wipet):
      // eine Source, 7 Layer.
      try {
        map.addSource(TRAILS_SOURCE_NAME, {
          type: 'geojson',
          data: fc,
        });
        for (const l of trailLayerSpec()) {
          map.addLayer(l as any);
        }
      } catch (err) {
        // Wave 14ca-fix64: war "warning + give up". Wenn das passiert
        // (Race: Style noch nicht ganz fertig, addSource wirft, oder eine
        // Source-ID kollidiert weil ein altes Style-Fragment haengen
        // geblieben ist) bleiben die Trails dauerhaft weg — bis zum
        // naechsten Style-Switch oder Trail-Update. User-Bug "Trails
        // verschwinden beim Style-Wechsel" → Retry einbauen statt Aufgeben.
        // Wir versuchen 3x mit ansteigendem Delay; wenn der Style sich in
        // dieser Zeit stabilisiert, kommen die Trails zurueck.
        console.warn('[map] Trails-Setup fehlgeschlagen, retry…', err);
        scheduleRetry();
      }
    };
    let retryAttempt = 0;
    let retryHandle: number | null = null;
    const scheduleRetry = () => {
      if (retryAttempt >= 3) {
        console.error('[map] Trails-Setup gibt nach 3 Retries auf');
        return;
      }
      retryAttempt++;
      const delay = 100 * Math.pow(2, retryAttempt - 1); // 100ms, 200ms, 400ms
      retryHandle = window.setTimeout(() => {
        retryHandle = null;
        // Map kann zwischenzeitlich unmountet sein — defensiv pruefen.
        if (!mapRef.current) return;
        if (!mapRef.current.isStyleLoaded()) {
          // Style noch nicht bereit, weiter warten.
          scheduleRetry();
          return;
        }
        apply();
      }, delay);
    };
    apply();
    return () => {
      if (retryHandle != null) window.clearTimeout(retryHandle);
    };
    // Wave 14ca-fix64: colorAssistMode in deps — Toggle-Flip rebuildet
    // FeatureCollection mit der neuen Palette (resolveTrailStyle liest
    // DIFFICULTY_COLOR runtime).
  }, [trails, mapReady, colorAssistMode]);

  // ULTRA: Renn-Route (3PBR) + 60-km-Offline-Korridor.
  //
  // Statische Daten (corridorData/routeData), aus GPX/3PBR_ALL_merged.gpx
  // vorgebaut. Idempotent nach dem Trails-Vorbild: Sources einmal addSource'n,
  // Layer einmal addLayer'n; bei einem Style-Switch (setStyle wiped alles) wird
  // beim nächsten Effect-Lauf neu aufgebaut. Retry, falls der Style beim ersten
  // Versuch noch nicht stabil ist.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    let retryAttempt = 0;
    let retryHandle: number | null = null;

    const apply = () => {
      const m = mapRef.current;
      if (!m) return;
      try {
        // Korridor + Navi-Segmente der AKTIVEN Region. Bei Region-Wechsel
        // setData auf die bestehende Source (kein Re-Add).
        const rmd = corridorOverlays ?? getRegionMapData(activeRegion);
        const corrSrc = m.getSource(CORRIDOR_SOURCE_ID) as
          | maplibregl.GeoJSONSource
          | undefined;
        if (!corrSrc) m.addSource(CORRIDOR_SOURCE_ID, { type: 'geojson', data: rmd.corridor });
        else corrSrc.setData(rmd.corridor);
        const navSrc = m.getSource(NAVI_SEGMENTS_SOURCE_ID) as
          | maplibregl.GeoJSONSource
          | undefined;
        if (!navSrc)
          m.addSource(NAVI_SEGMENTS_SOURCE_ID, { type: 'geojson', data: rmd.naviSegments });
        else navSrc.setData(rmd.naviSegments);
        for (const l of routeLayerSpec()) {
          if (!m.getLayer(l.id)) m.addLayer(l as never);
        }
      } catch (err) {
        // Style evtl. noch nicht fertig (Race nach setStyle) → kurz retrien.
        if (retryAttempt >= 3) {
          console.error('[map] ULTRA-Route-Setup gibt nach 3 Retries auf', err);
          return;
        }
        retryAttempt++;
        const delay = 100 * Math.pow(2, retryAttempt - 1);
        retryHandle = window.setTimeout(() => {
          retryHandle = null;
          if (!mapRef.current) return;
          if (!mapRef.current.isStyleLoaded()) {
            retryAttempt--; // dieser Versuch zählt nicht, Style war noch nicht da
          }
          apply();
        }, delay);
      }
    };
    apply();
    return () => {
      if (retryHandle != null) window.clearTimeout(retryHandle);
    };
  }, [mapReady, activeRegion, corridorOverlays]);

  // Wave 14bq: Start-/End-Marker für ALLE Trails (nicht nur den selektierten).
  //
  // User-Bug: "in der normalen anzeige sollte jeder trail das grüne dreieck am
  // anfang und das rote viereck am ende haben, nicht nur wenn man drauf klickt."
  //
  // Bis 14bq wurden die Marker ausschliesslich vom Selection-Overlay-Effect
  // (`trail-selection-endpoints` + `trail-selection-start` / `-end`) gezeichnet,
  // d. h. nur für den gerade selektierten Trail. Dieser Effect hier registriert
  // eine zweite GeoJSON-Source (`trail-endpoints-all`) mit je einem Start- und
  // End-Punkt pro Trail und rendert sie mit denselben Icons.
  //
  // Koexistenz mit dem Selection-Overlay: der selektierte Trail kriegt seine
  // Marker jetzt aus ZWEI Quellen (hier + Selection-Effect), die beide
  // dieselben Icons an denselben Koords rendern — visuell kein Unterschied.
  // Wir filtern bewusst NICHT den selektierten Trail raus, weil sonst ein
  // kurzer Frame entstünde, in dem beide Layer weg sind (Selection-Overlay-
  // Rebuild + "all"-Filter-Update sind nicht atomar synchronisiert).
  //
  // Icons werden idempotent via `hasImage()` registriert — gleiche Logik
  // wie im Selection-Effect, Duplikation ist bewusst in Kauf genommen, damit
  // dieser Effect unabhängig vom Selection-Effect funktioniert (z. B. wenn
  // gar nichts selektiert ist).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const apply = () => {
      // --- Icons registrieren (idempotent). Kopie aus dem Selection-Effect,
      // damit dieser Effect ohne Selection funktionsfähig ist. Bei setStyle()
      // wird die Image-Registry geleert → beim nächsten mapReady-Tick erneut
      // hinzufügen.
      const PT_SIZE = 28;
      const makeCanvas = (size: number) => {
        const c = document.createElement('canvas');
        c.width = size;
        c.height = size;
        return c;
      };

      if (!map.hasImage('trail-start-triangle')) {
        const canvas = makeCanvas(PT_SIZE);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#22c55e';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.5;
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(PT_SIZE / 2, 4);
          ctx.lineTo(PT_SIZE - 4, PT_SIZE - 5);
          ctx.lineTo(4, PT_SIZE - 5);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          map.addImage('trail-start-triangle', ctx.getImageData(0, 0, PT_SIZE, PT_SIZE) as any, {
            pixelRatio: 2,
          });
        }
      }

      if (!map.hasImage('trail-end-square')) {
        const canvas = makeCanvas(PT_SIZE);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ef4444';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.5;
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.rect(5, 5, PT_SIZE - 10, PT_SIZE - 10);
          ctx.fill();
          ctx.stroke();
          map.addImage('trail-end-square', ctx.getImageData(0, 0, PT_SIZE, PT_SIZE) as any, {
            pixelRatio: 2,
          });
        }
      }

      // --- FeatureCollection: je ein Start- und End-Punkt pro Trail.
      // Trails mit <2 Punkten überspringen wir (keine sinnvolle Start/End-
      // Paar).
      const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
      for (const t of trails) {
        const geom = trackToGeoJson(t.track);
        const coords = geom.geometry.coordinates;
        if (coords.length < 2) continue;
        const start = coords[0]!;
        const end = coords[coords.length - 1]!;
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: start },
          properties: { role: 'start', trailId: t.id },
        });
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: end },
          properties: { role: 'end', trailId: t.id },
        });
      }
      const fc: GeoJSON.FeatureCollection<GeoJSON.Point> = {
        type: 'FeatureCollection',
        features,
      };

      // Source idempotent updaten: existierende Source kriegt setData(), neue
      // wird erstellt. Gleiche Strategie wie im trails-Effect für 'trails'.
      const existing = map.getSource('trail-endpoints-all') as
        | maplibregl.GeoJSONSource
        | undefined;
      if (existing) {
        existing.setData(fc);
      } else {
        try {
          map.addSource('trail-endpoints-all', { type: 'geojson', data: fc });
          // icon-size etwas kleiner als beim Selection-Overlay (dort 0.7–1.3),
          // damit bei vielen sichtbaren Trails die Karte nicht zugepflastert
          // wirkt. Bei weit rausgezoomter Map (zoom<10) werden die Marker
          // ganz ausgeblendet — sonst Punkt-Suppe.
          map.addLayer({
            id: 'trail-endpoints-all-start',
            type: 'symbol',
            source: 'trail-endpoints-all',
            minzoom: 10,
            filter: ['==', ['get', 'role'], 'start'],
            layout: {
              'icon-image': 'trail-start-triangle',
              'icon-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                10, 0.55,
                14, 0.85,
                16, 1.1,
              ],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'icon-anchor': 'center',
            },
          });
          map.addLayer({
            id: 'trail-endpoints-all-end',
            type: 'symbol',
            source: 'trail-endpoints-all',
            minzoom: 10,
            filter: ['==', ['get', 'role'], 'end'],
            layout: {
              'icon-image': 'trail-end-square',
              'icon-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                10, 0.55,
                14, 0.85,
                16, 1.1,
              ],
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'icon-anchor': 'center',
            },
          });
        } catch (err) {
          // Seltene Race: setStyle() läuft genau mitten in apply(). Beim
          // nächsten mapReady-Flip versuchen wir's nochmal — deswegen nur
          // warn, kein throw.
          console.warn('[map] endpoints-all-Setup fehlgeschlagen, retry kommt via mapReady', err);
        }
      }
    };
    apply();
  }, [trails, mapReady]);

  // Wave 14be: Click/Hover-Listener auf 'trails-hit' — EINMAL pro Map-Mount.
  // Gegensatz zur alten Impl (pro Trail ein eigener `map.on('click', …)`,
  // geleakt bei Trail-Wechseln) haben wir hier EINEN Listener, der die Trail-
  // ID aus `e.features[0].properties.id` holt. onTrailClick wird über den
  // Ref gelesen, damit Callback-Identity-Wechsel keine Re-Bindings auslösen.
  //
  // MapLibre akzeptiert map.on('click', layerId, fn) auch wenn der Layer
  // noch nicht existiert — die Bindung aktiviert sich, sobald der Layer
  // auftaucht. Deswegen ist es safe, das vor dem Trails-Effect zu mounten.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const onClick = (e: maplibregl.MapLayerMouseEvent) => {
      const id = e.features?.[0]?.properties?.id;
      if (id) onTrailClickRef.current?.(String(id));
    };
    const onEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const onLeave = () => {
      map.getCanvas().style.cursor = '';
    };
    map.on('click', TRAILS_HIT_LAYER_ID, onClick);
    map.on('mouseenter', TRAILS_HIT_LAYER_ID, onEnter);
    map.on('mouseleave', TRAILS_HIT_LAYER_ID, onLeave);
    return () => {
      map.off('click', TRAILS_HIT_LAYER_ID, onClick);
      map.off('mouseenter', TRAILS_HIT_LAYER_ID, onEnter);
      map.off('mouseleave', TRAILS_HIT_LAYER_ID, onLeave);
    };
  }, [mapReady]);

  // Selection-Overlay: Aura um den ausgewählten Trail + Richtungspfeile +
  // Start (grün) / End (rot) Marker.
  //
  // Wave 14ak: IDEMPOTENT-Refactor. Früher: cleanup() + addSource/addLayer
  // bei JEDER Dep-Änderung → sichtbares Flackern, wenn MapPage mehrmals in
  // Folge re-rendert (z. B. setSelected + setMode + setScrubMeters in
  // getrennten Batches). Außerdem: bei Runs mit vielen Sub-Segmenten änderte
  // sich `trails` (= mapTrails) in schneller Folge → mehrere Cleanup-Runden
  // → der zweite Trail sah man kurz gar nicht mehr.
  //
  // Jetzt: sources werden via `setData()` aktualisiert, layers nur beim
  // ersten Mal addLayer'd, danach wird nur noch `setPaintProperty` für die
  // aura-Farbe gesetzt. → Aura bleibt durchgehend sichtbar, egal wie oft
  // der Effect feuert. Cleanup läuft nur noch bei Ab-Wahl (selectedTrailId
  // null) und beim Unmount.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    // Alle Selection-Artefakte werden beim Ab-/Umwählen zuerst entfernt,
    // damit nichts doppelt oder veraltet bleibt. Die Aura-Layer-IDs aus
    // den alten Iterationen (14w 4-Layer, 14y 3-Layer) stehen hier weiter
    // drin — falls ein Dev-Hot-Reload den neuen Code in eine laufende
    // Map-Instanz einspielt, die noch die alten Layer hält.
    const SELECTION_LAYER_IDS = [
      // Aktuelle Single-Layer-Aura (Wave 14aa):
      'trail-selection-aura',
      // Legacy Multi-Layer-IDs — Hot-Reload-Safety:
      'trail-selection-aura-bloom',
      'trail-selection-aura-outer',
      'trail-selection-aura-mid',
      'trail-selection-aura-inner',
      'trail-selection-arrows',
      'trail-selection-end', // end unter start rendern falls sie zusammenfallen
      'trail-selection-start',
    ];
    const SELECTION_SOURCE_IDS = [
      'trail-selection-line',
      'trail-selection-aura-points', // Wave 14ab: MultiPoint-Source für Circle-Aura
      'trail-selection-endpoints',
    ];

    const cleanup = () => {
      // Wave 14ak-fix-2: kein isStyleLoaded-Gate — getLayer/removeLayer sind
      // idempotent-sicher: fehlt der Layer einfach, macht removeLayer nichts.
      // Ein Gate hier hätte zur Folge, dass beim Ab-Wählen während einer
      // Kamera-Animation (was oft zusammen passiert) das Cleanup schweigend
      // übersprungen wird und die Aura am alten Trail kleben bleibt.
      for (const id of SELECTION_LAYER_IDS) {
        try {
          if (map.getLayer(id)) map.removeLayer(id);
        } catch {
          /* map zerstört / style weg — egal */
        }
      }
      for (const id of SELECTION_SOURCE_IDS) {
        try {
          if (map.getSource(id)) map.removeSource(id);
        } catch {
          /* idem */
        }
      }
    };

    const apply = () => {
      // Wave 14ak-fix-2: KEIN `isStyleLoaded()`-Gate mehr. Das hat 5+ sec-
      // Verzögerungen verursacht, weil `isStyleLoaded` während jeder Camera-
      // Animation (fitBoundsToTrail läuft genau jetzt weil der User gerade
      // einen Trail geklickt hat!) und während Tile-Loads false zurückgibt.
      // Der Fallback `map.once('idle', apply)` feuerte dann erst wenn ALLE
      // Animationen + Tiles fertig waren → User wartet 5+ Sek auf die Aura.
      //
      // Der `mapReady`-Gate davor (Effect-Entry) reicht: sobald die Map ein
      // 'load' (bzw. 'style.load' nach setStyle) gefeuert hat, sind add/
      // setData/setPaintProperty-APIs sicher benutzbar. Kamera-Animationen
      // haben damit nichts zu tun.
      if (!selectedTrailId) {
        cleanup();
        return;
      }
      // Wave 14ah: Wenn ein Pseudo-Run selektiert ist, taucht er nicht in
      // `trails` auf (wir rendern dort die bunten Einzel-Segmente). Stattdessen
      // liefert MapPage einen selectionOverride mit dem kompletten Run-Track
      // und seiner (nominellen) Difficulty — genau das Material, das der
      // Selection-Overlay (Aura, Richtungspfeile, Start/End-Marker) braucht.
      const overrideTrail: { difficulty: Difficulty; colorHex?: string | null; track: GpxTrack } | null =
        selectionOverride ?? null;
      const trail = overrideTrail ?? trails.find((t) => t.id === selectedTrailId);
      if (!trail) {
        // Selection zeigt auf einen Trail der nicht in `trails` ist (z. B.
        // durch den Visibility-Filter gefiltert oder Daten noch nicht geladen).
        // Alte Aura abräumen, damit kein zombie-Overlay stehen bleibt.
        cleanup();
        return;
      }

      // Aura-Farbe aus der Schwierigkeit (oder Admin-Override). intermediate+
      // bekommt blau statt rot (dominante Stripe-Farbe), extreme schwarz;
      // alle anderen erben ihre DIFFICULTY_COLOR. colorOverride gewinnt —
      // wenn der Admin die Trail-Farbe manuell setzt, ziehen wir die Aura
      // mit.
      const auraColor = trail.colorHex || difficultyAuraColor(trail.difficulty);

      const feature = trackToGeoJson(trail.track);
      const coords = feature.geometry.coordinates;
      if (coords.length < 2) {
        cleanup();
        return;
      }
      // Length-Check oben garantiert ≥2 Punkte → [0] und [length-1] können
      // nicht undefined sein. tsc's noUncheckedIndexedAccess weiß das nicht.
      const start = coords[0]!;
      const end = coords[coords.length - 1]!;

      // Wave 14aa → 14ab:
      // Die GEGLÄTTETE (chamfered) Polyline dient jetzt nur noch der
      // Pfeil-Platzierung (symbol-placement: line). Die eigentliche Aura
      // läuft NICHT mehr über einen line-layer (Wave 14ab-Designwechsel,
      // siehe Begründung beim Aura-Layer unten), sondern über einen
      // circle-layer auf dicht abgetasteten Punkten entlang der Spur.
      //
      // Warum wir beides brauchen:
      //   - `chamferedFeature`  → arrows (symbol-placement: line). Pfeile
      //     sollen der GPS-Linie folgen, sichtbar gedreht werden.
      //   - `auraPoints`        → circle-aura. Punkte sind viel, klein,
      //     jeder rendert einen weichen Radial-Blob, die zusammen einen
      //     Schlauch formen. An Switchbacks verschmelzen Blobs
      //     benachbarter Arme ohne Capsule-Kollision.
      //
      // Die originale Trail-Linie (trailLayerSpec → Source 'trails',
      // Feature mit passender id-Property) bleibt rohe GPS-Spur —
      // geografisch exakt.
      const chamferedCoords = chamferPolyline(
        coords as [number, number][],
      );
      const chamferedFeature: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: chamferedCoords },
        properties: {},
      };
      const auraPoints = densifyPolyline(chamferedCoords);

      // Icons einmalig registrieren — setStyle() wirft die Image-Registry
      // weg, daher bei jedem Apply prüfen ob sie noch da sind.
      // Pfeil-Icon ist groß (40×40) und als Chevron (>) gezeichnet, damit
      // beide Schenkel symmetrisch links/rechts der Trail-Linie sichtbar sind.
      const ARROW_SIZE = 40;
      const PT_SIZE = 28;
      const makeCanvas = (size: number) => {
        const c = document.createElement('canvas');
        c.width = size;
        c.height = size;
        return c;
      };

      if (!map.hasImage('trail-arrow')) {
        const canvas = makeCanvas(ARROW_SIZE);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Chevron ">" — Apex am rechten Rand, Schenkel zurück nach links-oben
          // und links-unten. symbol-placement:line dreht das Icon in die Trail-
          // Richtung; Anchor ist der Icon-Mittelpunkt, daher läuft die Trail-Linie
          // mittig durch den Chevron, und die Schenkel ragen nach BEIDEN Seiten
          // raus (oben & unten der Linie). Schwarz + weißer Rand für Kontrast
          // auf farbigen Trail-Linien (rot/blau/schwarz/etc).
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 7;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(8, 6);
          ctx.lineTo(ARROW_SIZE - 6, ARROW_SIZE / 2);
          ctx.lineTo(8, ARROW_SIZE - 6);
          ctx.stroke();

          ctx.strokeStyle = '#0f172a';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.moveTo(8, 6);
          ctx.lineTo(ARROW_SIZE - 6, ARROW_SIZE / 2);
          ctx.lineTo(8, ARROW_SIZE - 6);
          ctx.stroke();

          map.addImage('trail-arrow', ctx.getImageData(0, 0, ARROW_SIZE, ARROW_SIZE) as any, {
            pixelRatio: 2,
          });
        }
      }

      if (!map.hasImage('trail-start-triangle')) {
        const canvas = makeCanvas(PT_SIZE);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Grünes Dreieck, Spitze nach oben — klassisches "Play/Start"-Symbol.
          ctx.fillStyle = '#22c55e';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.5;
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(PT_SIZE / 2, 4);
          ctx.lineTo(PT_SIZE - 4, PT_SIZE - 5);
          ctx.lineTo(4, PT_SIZE - 5);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          map.addImage('trail-start-triangle', ctx.getImageData(0, 0, PT_SIZE, PT_SIZE) as any, {
            pixelRatio: 2,
          });
        }
      }

      if (!map.hasImage('trail-end-square')) {
        const canvas = makeCanvas(PT_SIZE);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Rotes Quadrat mit weißem Rand — klassisches "Stop/Ende"-Symbol.
          ctx.fillStyle = '#ef4444';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2.5;
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.rect(5, 5, PT_SIZE - 10, PT_SIZE - 10);
          ctx.fill();
          ctx.stroke();
          map.addImage('trail-end-square', ctx.getImageData(0, 0, PT_SIZE, PT_SIZE) as any, {
            pixelRatio: 2,
          });
        }
      }

      // Wave 14ak: Sources idempotent aktualisieren. Wenn die Source schon da
      // ist, nur `setData` aufrufen — keine remove/add-Runde, damit keine
      // Frame-Lücke entsteht wo das Overlay weg ist.
      const lineData: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [chamferedFeature],
      };
      const auraData: GeoJSON.Feature = {
        type: 'Feature',
        geometry: { type: 'MultiPoint', coordinates: auraPoints },
        properties: {},
      };
      const endpointsData: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: start },
            properties: { role: 'start' },
          },
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: end },
            properties: { role: 'end' },
          },
        ],
      };

      const lineSrc = map.getSource('trail-selection-line') as
        | maplibregl.GeoJSONSource
        | undefined;
      if (lineSrc) {
        lineSrc.setData(lineData);
      } else {
        map.addSource('trail-selection-line', { type: 'geojson', data: lineData });
      }

      // Dicht abgetastete Punkte als MultiPoint-Feature — eine einzige
      // Feature mit N Koordinaten ist für MapLibre schlanker als N
      // Einzel-Features (weniger id-tracking, weniger event-bookkeeping).
      const auraSrc = map.getSource('trail-selection-aura-points') as
        | maplibregl.GeoJSONSource
        | undefined;
      if (auraSrc) {
        auraSrc.setData(auraData);
      } else {
        map.addSource('trail-selection-aura-points', {
          type: 'geojson',
          data: auraData,
        });
      }

      const epSrc = map.getSource('trail-selection-endpoints') as
        | maplibregl.GeoJSONSource
        | undefined;
      if (epSrc) {
        epSrc.setData(endpointsData);
      } else {
        map.addSource('trail-selection-endpoints', {
          type: 'geojson',
          data: endpointsData,
        });
      }

      // Aura (Wave 14ab — Circle-Layer auf dicht abgetasteten Punkten):
      //
      // Evolution dieses Codes:
      //   Wave 14w:  4-Layer-Stack mit line-blur. Blur-Shader an Segment-
      //     grenzen → Spikes in Kurven.
      //   Wave 14y:  3-Layer-Stack OHNE blur, runde Joins. Join-Fan-Triangles
      //     wurden an sehr scharfen Ecken als Dreiecke sichtbar.
      //   Wave 14aa: Polyline wurde via `chamferPolyline()` geglättet,
      //     Single-Line-Layer. Behob Join-Fans, aber: an eng gefalteten
      //     Switchbacks, wo zwei Trail-Arme nur wenige Meter auseinander
      //     liegen und die Aura 40+ px breit ist, kollidierten die
      //     Capsule-Enden der benachbarten Arme und produzierten ein
      //     sägezahn-förmiges „Zipper"-Muster. Das ist ein fundamentales
      //     Limit des Line-Layer-Konzepts — egal wie glatt die Polyline.
      //   Wave 14ab (hier): Aura NICHT mehr als Line rendern. Stattdessen
      //     wird die Polyline dicht abgetastet (densifyPolyline, ~9 m
      //     Punkte-Abstand) und jeder Punkt rendert einen weichen Radial-
      //     Gradient-Kreis (circle-blur: 1.0). Die Kreise überlappen sich
      //     und formen einen Schlauch ohne jegliche Capsule- oder Join-
      //     Geometrie. An Switchbacks verschmelzen die Blobs der benach-
      //     barten Arme organisch — keine Kollision, keine Kanten.
      //
      // Zoom-abhängige Opacity kompensiert den Overlap-Count: bei niedrigem
      // Zoom liegen viele Punkte in einem Circle-Radius (hohe Stapelung →
      // kleine α pro Circle), bei hohem Zoom wenige (niedrige Stapelung →
      // höhere α pro Circle). Ziel: sichtbare, aber nicht erdrückende
      // Aura über alle Zoom-Stufen.
      //
      // Warum die Trail-Linie selbst nicht chamfern: die farbige Linie aus
      // trailLayerSpec rendert aus der 'trails'-Source mit den ROHEN GPS-
      // Koords. Chamfer-Verschiebung liegt unter GPS-Präzision (~7 m),
      // trotzdem halten wir dort die exakten Rohdaten — wer aufnimmt möchte
      // die GPS-Originalspur sehen, nicht eine geglättete Kopie.
      // Wave 14ak: Layers nur einmal erstellen. Bei weiteren Effect-Runs
      // updaten wir lediglich die Aura-Farbe (setPaintProperty) — das ist das
      // einzige was sich zwischen Selektionen ändert. Alles andere (Radius,
      // Opacity, Symbols, Sizes) ist statisch.
      if (!map.getLayer('trail-selection-aura')) {
        map.addLayer({
          id: 'trail-selection-aura',
          type: 'circle',
          source: 'trail-selection-aura-points',
          paint: {
            'circle-color': auraColor,
            // Radius in Pixeln, zoom-abhängig. Bei hohem Zoom breiter, damit
            // die Aura deutlich sichtbar bleibt; bei weitem Zoom kleiner, weil
            // der Trail dort ohnehin nur wenige Pixel misst.
            'circle-radius': [
              'interpolate', ['linear'], ['zoom'],
              8, 8,
              12, 18,
              16, 32,
              20, 52,
            ],
            // 1.0 = Alpha fadet LINEAR vom Mittelpunkt (volle circle-opacity)
            // bis zum Rand (0). Zusammen mit den dicht überlappenden Kreisen
            // ergibt das einen weichen, gleichmäßigen Schlauch.
            'circle-blur': 1.0,
            // Exponentielle Interpolation, weil die Anzahl überlappender
            // Kreise pro Pixel ungefähr exponentiell mit dem Zoom skaliert
            // (pixels_per_meter ~ 2^z). Werte empirisch gewählt, sodass der
            // Kern des Schlauchs ca. 0.35 effektive Opacity über alle Zooms
            // zeigt — klar sichtbar, ohne die Karte drunter zu verdecken.
            'circle-opacity': [
              'interpolate', ['exponential', 1.8], ['zoom'],
              8, 0.004,
              12, 0.025,
              16, 0.12,
              20, 0.32,
            ],
            'circle-stroke-width': 0,
          },
        });
      } else {
        // Farbe evtl. geändert (Trail-Wechsel z. B. von blau zu schwarz).
        map.setPaintProperty('trail-selection-aura', 'circle-color', auraColor);
      }

      // Richtungspfeile als Chevron (>) entlang der Linie, zentriert auf
      // der Linie (kein icon-offset). Dadurch stehen die beiden Schenkel des
      // Chevrons symmetrisch links/rechts der Trail-Linie.
      if (!map.getLayer('trail-selection-arrows')) {
        map.addLayer({
          id: 'trail-selection-arrows',
          type: 'symbol',
          source: 'trail-selection-line',
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 110,
            'icon-image': 'trail-arrow',
            'icon-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 0.8,
              14, 1.1,
              16, 1.3,
            ],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-rotation-alignment': 'map',
            'icon-pitch-alignment': 'map',
          },
        });
      }

      // Start-Marker: grünes Dreieck (Play-Symbol).
      if (!map.getLayer('trail-selection-start')) {
        map.addLayer({
          id: 'trail-selection-start',
          type: 'symbol',
          source: 'trail-selection-endpoints',
          filter: ['==', ['get', 'role'], 'start'],
          layout: {
            'icon-image': 'trail-start-triangle',
            'icon-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 0.7,
              16, 1.3,
            ],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-anchor': 'center',
          },
        });
      }

      // End-Marker: rotes Quadrat (Stop-Symbol).
      if (!map.getLayer('trail-selection-end')) {
        map.addLayer({
          id: 'trail-selection-end',
          type: 'symbol',
          source: 'trail-selection-endpoints',
          filter: ['==', ['get', 'role'], 'end'],
          layout: {
            'icon-image': 'trail-end-square',
            'icon-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 0.7,
              16, 1.3,
            ],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-anchor': 'center',
          },
        });
      }
    };

    apply();
    // Wave 14ak-fix: KEIN return cleanup hier! Früher (bis einschl. Wave 14aj)
    // stand hier `return cleanup`, was React dazu brachte, bei JEDEM Dep-
    // Wechsel (selectedTrailId, selectionOverride, trails, mapReady) zuerst
    // ALLE Aura-Sources + Layer abzureißen, bevor der neue Effect-Run sie
    // wieder aufbaut. Das erzeugte einen sichtbaren "off"-Frame — bei
    // mehreren Dep-Wechseln in Folge entstand der neonlicht-artige Flicker.
    //
    // Jetzt: der Effect ist idempotent (setData / hasLayer-Guards). Die Aura
    // bleibt durchgehend auf der Map, Dep-Änderungen triggern nur stille
    // Updates. Cleanup wird nur noch INNERHALB von apply() aufgerufen, wenn
    // selectedTrailId null ist oder der Trail verschwindet.
    //
    // Kein Memory-Leak: beim echten Unmount wird die MapLibre-Map-Instanz
    // komplett zerstört (s. unmount-Effect weiter oben), damit gehen die
    // Layer+Sources sowieso mit weg.
    //
    // Wave 14ca-fix64: colorAssistMode in deps — Selection-Aura nutzt
    // difficultyAuraColor() das aus DIFFICULTY_COLOR liest. Ohne Re-Run
    // bleibt die Aura beim Toggle-Flip in der alten Farbe haengen.
  }, [selectedTrailId, selectionOverride, trails, mapReady, colorAssistMode]);

  // ---------------------------------------------------------------------
  // Wave 14cf — Ride-Next-Aura.
  //
  // Auf komplexen Routen (Loops, mehrere Kreuzungen am gleichen Punkt) ist
  // im Ride-Mode nicht klar wo's weitergeht. Wir highlighten daher die
  // nächsten N Meter ab GPS-Position als orange Aura — unmissverständlich
  // sichtbar selbst auf gemischten Trail-Bündeln.
  //
  // Source: 'ride-next-aura-points' (MultiPoint, dicht abgetastet).
  // Layer:  'ride-next-aura' (Circle, gleiches Stack-Pattern wie
  //          trail-selection-aura, nur orange + leicht kleiner Radius).
  //
  // Trigger:
  //   - Bei jedem GPS-Tick (currentPosition) Re-slice + setData.
  //   - Bei Track-Wechsel (selectionOverride / selectedTrailId).
  //   - Bei Pref-Wechsel (rideNextAuraMeters).
  //
  // Off-track-Logik: wenn der User > 100m von der Route abweicht, blenden
  // wir die Aura aus — auf der Distanz ist sie irreführend (zeigt eine
  // Position auf der Route, die der User aber nicht innehat).
  // ---------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const SOURCE_ID = 'ride-next-aura-points';
    const LAYER_ID = 'ride-next-aura';
    const HALO_ID = 'ride-next-aura-halo';

    const cleanup = () => {
      // Wave 14cf-fix2: zwei Layer (Body + Halo) entfernen — Halo zuerst
      // weil er als beforeId-Anker für Body dient, Reihenfolge egal aber
      // konsistent.
      for (const id of [LAYER_ID, HALO_ID]) {
        try {
          if (map.getLayer(id)) map.removeLayer(id);
        } catch {
          /* ignore */
        }
      }
      try {
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        /* ignore */
      }
    };

    // Aus welcher Track-Quelle ziehen wir die Geometrie?
    //   1. selectionOverride.track (Custom-Tour-Pin-Route, Run-Aggregat).
    //   2. Fallback auf selectedTrailId-passenden Trail aus `trails`.
    let track: GpxTrack | null = null;
    if (selectionOverride?.track && selectionOverride.track.points.length >= 2) {
      track = selectionOverride.track;
    } else if (selectedTrailId) {
      const t = trails.find((tt) => tt.id === selectedTrailId);
      if (t && t.track.points.length >= 2) track = t.track;
    }

    // Vorbedingungen für die Aura: Pref > 0, Track + GPS-Position vorhanden.
    if (
      rideNextAuraMeters <= 0 ||
      !track ||
      !currentPosition ||
      !Number.isFinite(currentPosition.latitude) ||
      !Number.isFinite(currentPosition.longitude)
    ) {
      cleanup();
      return;
    }

    // GPS auf Track snappen — wenn off-track > 100m: keine Aura (irreführend).
    const cum = cumulativeDistances(track.points);
    const total = cum[cum.length - 1] ?? 0;
    if (total <= 0) {
      cleanup();
      return;
    }
    // Wave 14cf-fix5: Monotone Progression auch hier — sonst kann der
    // orange Highlight auf einer Route mit gemeinsamen Abschnitten zur
    // FALSCHEN Stelle springen (User-Bug: an einer Kreuzung wurde der
    // bereits-gefahrene Abzweig hervorgehoben statt des kommenden).
    // Wir tracken den letzten alongTrackMeters per ref. Bei Track-Wechsel
    // resetten wir ihn (anderer Track → alter Hint ungültig).
    const trackKey =
      selectionOverride?.track === track
        ? `override:${selectedTrailId ?? 'override'}`
        : (selectedTrailId ?? '');
    if (rideNextHintRef.current.trackKey !== trackKey) {
      rideNextHintRef.current = { trackKey, meters: null };
    }
    const snap = nearestPointOnTrack(
      track.points,
      currentPosition.latitude,
      currentPosition.longitude,
      cum,
      { hintAlongMeters: rideNextHintRef.current.meters },
    );
    if (snap.offTrackMeters > 100) {
      cleanup();
      return;
    }
    // Hint für nächsten Render aktualisieren.
    rideNextHintRef.current = {
      trackKey,
      meters: snap.alongTrackMeters,
    };

    // Slice-Bereich [from, to] entlang Track-Länge.
    const from = snap.alongTrackMeters;
    const to = Math.min(total, from + rideNextAuraMeters);
    if (to <= from + 1) {
      cleanup();
      return;
    }

    const slicedCoords = sliceTrackBetween(track.points, from, to, cum);
    if (slicedCoords.length < 2) {
      cleanup();
      return;
    }

    // Wave 14cf-fix4 — Orange-Aura → Orange-Linie.
    //
    // User-Feedback (Bild 1798): "Die orangene Aura ist totaler Quatsch,
    // sie ist so undurchsichtig, dass sie keine 'Aura' mehr ist, sie
    // verdeckt jeglichen Sicht auf die Wege etc. Mach doch einfach den
    // Strich orange, und die Aura blau wie sie war."
    //
    // Vorher (fix3): Densified MultiPoint + Circle-Layer mit hohem
    // Opacity → fette orange Wurst, die das darunter liegende Trail-
    // Geflecht komplett überdeckte.
    //
    // Jetzt (fix4): Schmale Linie genau auf der Route, die die nächsten
    // N Meter markiert. Die wide Selection-Aura (blau, weiterhin Circle-
    // Layer) bleibt unangetastet und gibt den weichen Glow-Korridor.
    // User sieht damit: blauer Korridor = ganze Route, orange dünner
    // Strich darin = die unmittelbare Fahrtrichtung.
    //
    // Wir nutzen ein einziges LineString-Feature aus der ungesliceten
    // Polyline (kein Densify nötig, MapLibre Line-Renderer interpoliert
    // selbst). Das spart Setup-Zeit und Memory.
    const featureCollection = {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: slicedCoords,
          },
          properties: {},
        },
      ],
    };

    const existingSource = map.getSource(SOURCE_ID) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (existingSource) {
      existingSource.setData(featureCollection as any);
    } else {
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: featureCollection as any,
      });
    }

    if (!map.getLayer(LAYER_ID)) {
      // Layer-Order: orange Linie ÜBER der Selection-Aura (sichtbar im
      // blauen Glow-Korridor) UND ÜBER der difficulty-colored Trail-Linie
      // (überschreibt sie auf der Next-N-Sektion). Pfeile + Start/End-
      // Marker bleiben ganz oben — wir hängen orange direkt darunter ein.
      const beforeId = map.getLayer('trail-selection-arrows')
        ? 'trail-selection-arrows'
        : map.getLayer('trail-selection-start')
          ? 'trail-selection-start'
          : undefined;

      // Halo: schmale weiße Linie ETWAS BREITER als die orange — wirkt
      // als feiner Outline und erhält den Kontrast wenn die orange
      // Linie auf einer gleichfarbigen Stelle (z. B. helles Tile-Feld)
      // liegt. Dünn genug damit das Halo nicht selbst die Karte verdeckt.
      map.addLayer(
        {
          id: HALO_ID,
          type: 'line',
          source: SOURCE_ID,
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#ffffff',
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              8, 4,
              12, 7,
              16, 11,
              20, 15,
            ],
            'line-opacity': 0.85,
          },
        },
        beforeId,
      );
      // Body: das eigentliche orange. Etwas dünner als das Halo, scharf
      // und gesättigt. Diese Linie ist der "wo geht's lang"-Marker.
      map.addLayer(
        {
          id: LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#f97316',
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              8, 2.5,
              12, 4.5,
              16, 7,
              20, 10,
            ],
            'line-opacity': 1.0,
          },
        },
        beforeId,
      );
    }

    return () => {
      // Bewusst KEIN cleanup() im Effekt-Return — der nächste Lauf nutzt
      // setData() (kein Layer-Tear-Down), das vermeidet Flickern bei
      // hochfrequenten GPS-Updates. Cleanup übernimmt der Unmount.
    };
  }, [
    rideNextAuraMeters,
    selectedTrailId,
    selectionOverride,
    trails,
    currentPosition,
    mapReady,
  ]);

  return <div ref={containerRef} className="absolute inset-0" />;
}
